import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface OwnProps {
  text: string;
  title?: string;
}

const StyledRoot = styled.span`
  position: relative;
  display: inline-flex;
`;

const StyledToggle = styled.button`
  width: 14px;
  height: 14px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.panel};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
`;

const StyledPopover = styled.div`
  position: absolute;
  top: 18px;
  left: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
  width: 240px;
  padding: 0.5rem 0.625rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: ${({ theme }) => theme.radii.sm};
  box-shadow: ${({ theme }) => theme.shadows.paletteHover};
  font-size: 11.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 400;
  white-space: normal;
`;

const StyledPopoverTitle = styled.div`
  font-weight: 700;
  margin-bottom: 3px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

/**
 * Small (i) icon that toggles a popover with descriptive help text on click,
 * and shows the same text as a native tooltip on hover.
 */
const InfoHint = ({ text, title }: OwnProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);

    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!text) return null;

  return (
    <StyledRoot ref={ref}>
      <StyledToggle type='button' onClick={handleToggle} title={text} aria-label={title ?? 'Help'}>
        i
      </StyledToggle>
      {open && (
        <StyledPopover>
          {title && <StyledPopoverTitle>{title}</StyledPopoverTitle>}
          {text}
        </StyledPopover>
      )}
    </StyledRoot>
  );
};

const MemoInfoHint = React.memo(InfoHint);

export { MemoInfoHint as InfoHint };
export default MemoInfoHint;
